import React, { forwardRef, useId, useMemo } from 'react';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

import { RadioGroupContext } from './context';

export interface RadioGroupProps {
  /** The `value` of the checked Radio, or `null` when none is checked. The group is always controlled. */
  value: string | null;
  /** Fired with the change event of the Radio that became checked, and that Radio's `value`. */
  onChange: (event: React.ChangeEvent<HTMLInputElement>, value: string) => void;
  /**
   * The `name` every Radio in the group shares. The browser groups radios by name, which gives the
   * group one Tab stop and arrow-key movement. A unique name is generated when it is omitted; pass
   * one only when a form submission needs it.
   */
  name?: string;
  /** Disables every Radio in the group. */
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-describedby'?: string;
  children: React.ReactNode;
}

/**
 * The canonical way to render a set of Radios that form one choice: an element with
 * `role="radiogroup"` that hands its Radios a shared name, the checked value and the change handler.
 *
 * It holds no keyboard code. The Radios are native inputs that share a name, so the browser gives
 * the group one Tab stop and moves the selection with the arrow keys.
 */
const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      value,
      onChange,
      name,
      disabled = false,
      className,
      id,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      children,
      ...restProps
    },
    ref
  ) => {
    const generatedName = useId();

    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to RadioGroup: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the RadioGroupProps interface and to the component's signature.`,
        true
      );
    }

    const contextValue = useMemo(
      () => ({ name: name ?? generatedName, value, onChange, disabled }),
      [name, generatedName, value, onChange, disabled]
    );

    return (
      <RadioGroupContext.Provider value={contextValue}>
        <div
          ref={ref}
          role="radiogroup"
          data-fs-component="radio-group"
          id={id}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-describedby={ariaDescribedBy}
          aria-disabled={disabled || undefined}
          className={className}
        >
          {children}
        </div>
      </RadioGroupContext.Provider>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
